'use client';

import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, SidebarIcon } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import type { PaddingBox } from '../_shared/resolvePadding';
import { SectionShell } from '../_shared/SectionShell';
import { useMounted } from '../_shared/useMounted';

// ─── BFF response shapes ──────────────────────────────────────────────────────

interface SummaryResponse {
  score?: number;
  totalPoints?: number;
  rank?: number;
  pointName?: string;
  levelName?: string;
  levelCode?: string;
  levelPercent?: number;
  badgeCount?: number;
  streakDays?: number;
  pointsToNextLevel?: number;
  nextLevelName?: string;
  progressPercent?: number;
}

interface BadgeMetadata {
  id?: string;
  name?: string;
  description?: string;
  iconUrl?: string;
}

interface BadgeResponse {
  id?: string | number;
  elementId?: string;
  name?: string;
  iconUrl?: string;
  metadata?: BadgeMetadata;
  total?: number;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface GamificationPanelV3Props {
  /** Author-supplied copy overrides (labels / locale). Data always comes from API. */
  heading?: string;
  levelLabel?: string;
  pointsTitle?: string;
  badgesTitle?: string;

  /** Link targets for the two headline segments. */
  pointsHref?: string;
  badgesHref?: string;

  /** Decorative background glow tone. `multi` layers a second accent in the
   *  opposite corner; `none` removes the glow entirely. Theme-aware. */
  glow?: 'violet' | 'teal' | 'amber' | 'multi' | 'none';

  /** Colour treatment. `color` (default) uses the violet/teal/amber
   *  gamification accents; `mono` renders the whole band in grayscale
   *  slate tones for a restrained, monochrome look. */
  design?: 'color' | 'mono';

  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  style?: CSSProperties;
}

// ─── Config ─────────────────────────────────────────────────────────────────

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
const bffUrl = (path: string) => `${BASE}${path}`;

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * GamificationPanelV3 — full-width, single-row gamification band.
 *
 * A horizontal "cockpit" strip (theme-aware, gamified) that packs identity +
 * level progress on the left, then the two headline totals — Points and
 * Badges (with a live badge count) — as colour-coded segments on the
 * right. Designed to span the full content width rather than sit as a
 * compact card (that's what V2 is for). All figures come from the live
 * gamification API. Auth-gated by default.
 */
export function GamificationPanelV3({
  heading: headingProp,
  levelLabel: levelLabelProp,
  pointsTitle: pointsTitleProp,
  badgesTitle: badgesTitleProp,
  pointsHref = '/lxp/gamification',
  badgesHref = '/lxp/gamification',
  glow = 'violet',
  design = 'color',
  requireAuth,
  paddingOuter,
  paddingInner,
  style,
}: GamificationPanelV3Props) {
  const currentUser = useCurrentUser<{
    name?: string;
    surname?: string;
    initials?: string;
    avatar?: string;
  }>();
  const cls = `lms-gamif3-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const mounted = useMounted();
  const { t, i18n } = useTranslation('common');

  // Author-supplied copy wins; otherwise fall back to the shared translation
  // catalog so every label follows the active locale instead of a hardcoded
  // Turkish literal. Keys mirror the ones used across the profile blocks.
  const heading = headingProp || t('title.learningJourney');
  const levelLabel = levelLabelProp || t('label.level');
  const pointsTitle = pointsTitleProp || t('achievements.totalPoints');
  const badgesTitle = badgesTitleProp || t('title.badges');

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [badges, setBadges] = useState<BadgeResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const abort = useRef<AbortController | null>(null);

  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false);
      return;
    }

    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLoading(true);

    Promise.all([
      fetch(bffUrl('/api/gamification/summary'), { signal: ctrl.signal })
        .then<SummaryResponse | null>((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(bffUrl('/api/gamification/all-badges'), { signal: ctrl.signal })
        .then<BadgeResponse[]>((r) => (r.ok ? r.json() : []))
        .catch(() => []),
    ]).then(([sum, bdg]) => {
      if (ctrl.signal.aborted) return;
      setSummary(sum);
      setBadges(Array.isArray(bdg) ? bdg : []);
      setLoading(false);
    });

    return () => ctrl.abort();
  }, [isSignedIn]);

  // Hold until mounted so the auth-gated / user-dependent render can't diverge
  // between the server (no redux-persist user) and the first client render.
  if (!mounted) return null;
  if ((gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn)) return null;

  // ── Derived display values ────────────────────────────────────────────────

  const score = summary?.score ?? summary?.totalPoints;
  const levelName = summary?.levelName;
  const bar = clamp(summary?.progressPercent ?? summary?.levelPercent ?? 0);

  // "Earn N more points to reach the next level" — only meaningful while a
  // next level exists (the API omits pointsToNextLevel at the top of the
  // ladder). A tenant-renamed point unit is used verbatim; the catalog
  // fallback is lowercased so it reads as part of the sentence.
  const pointsLeft = summary?.pointsToNextLevel;
  const pointUnit =
    summary?.pointName ?? t('gamification.pointName').toLocaleLowerCase(i18n.language);
  const nextLevelHint =
    typeof pointsLeft === 'number' && pointsLeft > 0
      ? t('gamification.pointsToNextLevel', {
          points: pointsLeft.toLocaleString(i18n.language),
          pointName: pointUnit,
        })
      : null;

  // Badge count: prefer the API's own tally, else count earned badges.
  const earnedCount = badges.filter((b) => (b.total ?? 0) > 0).length;
  const badgeCount = summary?.badgeCount ?? earnedCount;

  // earned first — max 4 icons shown in the strip (matches the mock)
  const badgeRow = [...badges].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).slice(0, 4);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <SectionShell
      maxWidth="theme"
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      style={style}
    >
      <section className={`${cls}-root`}>
        <style dangerouslySetInnerHTML={{ __html: css(cls, bar, glow, design) }} />

        {loading ? (
          <Skeleton cls={cls} />
        ) : (
          <Card className={`${cls}-card`}>
            {/* ── Identity + level progress ─────────────────────────────── */}
            <div className={`${cls}-id`}>
              <div className={`${cls}-ring`}>
                <div className={`${cls}-ringInner`}>
                  {currentUser?.avatar ? (
                    <img
                      src={currentUser.avatar}
                      alt={currentUser.name ?? ''}
                      className={`${cls}-avatarImg`}
                    />
                  ) : (
                    <span className={`${cls}-avatarInit`}>
                      {currentUser?.initials ?? currentUser?.name?.slice(0, 2).toUpperCase() ?? '?'}
                    </span>
                  )}
                </div>
                {summary?.levelCode && <b className={`${cls}-lvlChip`}>{summary.levelCode}</b>}
              </div>

              <div className={`${cls}-idText`}>
                <div className={`${cls}-lvlName`}>{levelName ?? heading}</div>
                <div className={`${cls}-bar`}>
                  <i />
                </div>
                <div className={`${cls}-lvlMeta`}>
                  {levelLabel} %{bar}
                  {summary?.nextLevelName && (
                    <span className={`${cls}-next`}> · {summary.nextLevelName}</span>
                  )}
                </div>
                {nextLevelHint && <div className={`${cls}-lvlHint`}>{nextLevelHint}</div>}
              </div>
            </div>

            <div className={`${cls}-divider`} />

            {/* ── Points total ──────────────────────────────────────────── */}
            <a className={`${cls}-stat`} href={bffUrl(pointsHref)}>
              <div className={`${cls}-statLabel`}>{pointsTitle}</div>
              <div className={`${cls}-statBody`}>
                <span className={`${cls}-statIco tone-gold`}>
                  <SidebarIcon name="zap" size={18} />
                </span>
                <div>
                  <div className={`${cls}-statNum`}>{(score ?? 0).toLocaleString()}</div>
                  <div className={`${cls}-statSub`}>{summary?.pointName ?? t('label.point')}</div>
                </div>
              </div>
            </a>

            <div className={`${cls}-divider`} />

            {/* ── Badges: count + icon strip ────────────────────────────── */}
            <a className={`${cls}-stat`} href={bffUrl(badgesHref)}>
              <div className={`${cls}-statLabel`}>{badgesTitle}</div>
              <div className={`${cls}-statBody`}>
                <span className={`${cls}-statIco tone-violet`}>
                  <SidebarIcon name="award" size={18} />
                </span>
                <div className={`${cls}-badgeVal`}>
                  <div className={`${cls}-statNum`}>{badgeCount}</div>
                  <div className={`${cls}-statSub`}>{t('label.badge')}</div>
                </div>
                {badgeRow.length > 0 && (
                  <div className={`${cls}-badgeStrip`}>
                    {badgeRow.map((b, i) => {
                      const earned = (b.total ?? 0) > 0;
                      const name = b.metadata?.name ?? b.name ?? '';
                      const iconUrl = b.metadata?.iconUrl ?? b.iconUrl;
                      return (
                        <span
                          key={b.id ?? b.elementId ?? i}
                          className={`${cls}-badge${earned ? '' : ' locked'}`}
                          title={name}
                        >
                          {iconUrl ? (
                            <img src={iconUrl} alt={name} />
                          ) : (
                            <SidebarIcon name="award" size={16} />
                          )}
                          {(b.total ?? 0) > 1 && <b>{b.total}</b>}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </a>
          </Card>
        )}
      </section>
    </SectionShell>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(n: number): number {
  return Math.max(0, Math.min(100, Math.round(n)));
}

type GlowTone = NonNullable<GamificationPanelV3Props['glow']>;

/**
 * Resolve a glow preset into the two radial-glow colours (top-right primary +
 * bottom-left secondary) for a given theme. Opacities mirror the original
 * hardcoded values (dark .20 / light .14); the secondary sits a touch softer.
 */
function glowColors(glow: GlowTone, light: boolean, mono = false): { g1: string; g2: string } {
  const P = light ? 0.14 : 0.2;
  const S = light ? 0.1 : 0.16;
  const rgba = (rgb: string, a: number) => `rgba(${rgb},${a})`;
  // In mono every accent collapses onto a single slate glow, so the corners
  // read as a soft neutral wash rather than a coloured bloom.
  const SLATE = '148,163,184';
  const VIOLET = mono ? SLATE : '139,92,246';
  const TEAL = mono ? SLATE : '34,211,238';
  const AMBER = mono ? SLATE : '245,158,11';
  switch (glow) {
    case 'teal':
      return { g1: rgba(TEAL, P), g2: 'transparent' };
    case 'amber':
      return { g1: rgba(AMBER, P), g2: 'transparent' };
    case 'multi':
      return { g1: rgba(VIOLET, P), g2: rgba(TEAL, S) };
    case 'none':
      return { g1: 'transparent', g2: 'transparent' };
    case 'violet':
    default:
      return { g1: rgba(VIOLET, P), g2: 'transparent' };
  }
}

function css(
  cls: string,
  bar: number,
  glow: GlowTone,
  design: GamificationPanelV3Props['design'] = 'color',
): string {
  const mono = design === 'mono';
  // ── Gamified "cockpit" palette ────────────────────────────────────────
  // A self-contained band (matches the mock) whose surface follows the
  // portal's light/dark theme (`:root[data-theme]`), while the colourful
  // accents carry the gamification tone in both modes. Surface / text /
  // line colours are CSS variables so a single dark override block flips
  // the whole widget; the accent gradients stay fixed across themes.
  //
  // The base MUST be the light palette, not the dark one — the app-wide
  // contract (see the portal's `globals.css`) is "no `data-theme` attribute
  // ⇒ light". That attribute is stamped client-side by the `<head>` theme
  // bootstrap, and a root-layout re-render (e.g. `revalidatePath('/',
  // 'layout')` from the language switcher) can drop it without re-running
  // that script. Defaulting to dark made this band flip to dark while the
  // rest of the page stayed light.
  // Accent palette. `mono` swaps the three gamification hues for a slate
  // ramp (light→dark) so every gradient, ring, bar, icon and badge that
  // references these constants renders in grayscale without touching the
  // theme-aware surface variables below.
  const AMBER = mono ? '#94a3b8' : '#f59e0b';
  const AMBER_L = mono ? '#cbd5e1' : '#fbbf24';
  const VIOLET = mono ? '#cbd5e1' : '#8b5cf6';
  const VIOLET_D = mono ? '#94a3b8' : '#7c3aed';
  const TEAL = mono ? '#e2e8f0' : '#22d3ee';
  const TEAL_D = mono ? '#94a3b8' : '#0891b2';
  // Level chip sits on the amber/slate gradient — dark brown ink reads on
  // gold, dark slate ink reads on the grey chip.
  const CHIP_INK = mono ? '#1e293b' : '#3a2606';

  const glowDark = glowColors(glow, false, mono);
  const glowLight = glowColors(glow, true, mono);

  // Surface + ink. `mono` trades the navy cockpit for neutral zinc greys
  // (dark → charcoal, light → off-white) and neutral ink, so the whole
  // band reads grayscale rather than just recolouring the accents.
  const INK = mono ? '#fafafa' : '#f8fafc';
  const MUT = mono ? 'rgba(228,228,231,.62)' : 'rgba(226,232,240,.62)';
  const CARD_A = mono ? '#26262b' : '#1c2138';
  const CARD_B = mono ? '#161619' : '#141829';
  const INK_L = mono ? '#18181b' : '#0f172a';
  const MUT_L = mono ? 'rgba(82,82,91,.80)' : 'rgba(71,85,105,.80)';
  const CARD_B_L = mono ? '#f4f4f5' : '#eef2f7';

  return `
.${cls}-root{width:100%;margin:0;
  /* light cockpit — default, matching the app-wide "no data-theme ⇒ light" rule */
  --g3-ink:${INK_L};
  --g3-mut:${MUT_L};
  --g3-card-a:#ffffff;
  --g3-card-b:${CARD_B_L};
  --g3-line:rgba(15,23,42,.10);
  --g3-chip:rgba(15,23,42,.05);
  --g3-track:rgba(15,23,42,.08);
  --g3-ring-track:rgba(15,23,42,.12);
  --g3-glow:${glowLight.g1};
  --g3-glow2:${glowLight.g2};
  --g3-skel:rgba(15,23,42,.07)}

/* ── Dark theme override — navy/charcoal cockpit, light ink ──────────── */
:root[data-theme='dark'] .${cls}-root{
  --g3-ink:${INK};
  --g3-mut:${MUT};
  --g3-card-a:${CARD_A};
  --g3-card-b:${CARD_B};
  --g3-line:rgba(148,163,184,.16);
  --g3-chip:rgba(148,163,184,.10);
  --g3-track:rgba(148,163,184,.18);
  --g3-ring-track:rgba(148,163,184,.22);
  --g3-glow:${glowDark.g1};
  --g3-glow2:${glowDark.g2};
  --g3-skel:rgba(148,163,184,.16)}

/* ── One full-width horizontal band. Rendered as <Card>: border + elevation
      come from the component (ring-(--color-border-brand) + shadow-(--shadow-brand)),
      same as every other card. Only flex-direction:row is spelled out here to
      beat <Card>'s flex-col; the gradient, radius and glow stay its own. ── */
.${cls}-card{position:relative;box-sizing:border-box;width:100%;border-radius:18px;
  padding:16px 22px;display:flex;flex-direction:row;align-items:center;gap:22px;flex-wrap:wrap;overflow:hidden;
  background:linear-gradient(135deg, var(--g3-card-a), var(--g3-card-b))}
.${cls}-card::before{content:"";position:absolute;top:-40%;right:-6%;width:280px;height:280px;
  border-radius:999px;pointer-events:none;
  background:radial-gradient(circle, var(--g3-glow), transparent 70%)}
.${cls}-card::after{content:"";position:absolute;bottom:-40%;left:-6%;width:280px;height:280px;
  border-radius:999px;pointer-events:none;
  background:radial-gradient(circle, var(--g3-glow2), transparent 70%)}

/* ── Identity + level progress ───────────────────────────────────────── */
.${cls}-id{position:relative;display:flex;align-items:center;gap:14px;flex:1 1 260px;min-width:220px}
.${cls}-ring{position:relative;width:56px;height:56px;border-radius:999px;flex-shrink:0;padding:2.5px;
  background:conic-gradient(from -90deg, ${VIOLET}, ${TEAL} 55%, ${AMBER_L} ${bar}%, var(--g3-ring-track) 0)}
.${cls}-ringInner{width:100%;height:100%;border-radius:999px;overflow:hidden;display:grid;place-items:center;
  background:linear-gradient(150deg, ${VIOLET}, ${TEAL});border:2.5px solid var(--g3-card-a)}
.${cls}-avatarImg{width:100%;height:100%;object-fit:cover}
.${cls}-avatarInit{color:#fff;font-weight:800;font-size:19px;line-height:1}
.${cls}-lvlChip{position:absolute;bottom:-5px;left:50%;transform:translateX(-50%);
  background:linear-gradient(135deg, ${AMBER_L}, ${AMBER});color:${CHIP_INK};font-size:9px;font-weight:900;
  padding:1.5px 7px;border-radius:999px;font-style:normal;white-space:nowrap;box-shadow:0 0 0 2px var(--g3-card-a)}
.${cls}-idText{min-width:0;flex:1 1 auto}
.${cls}-lvlName{font-size:16px;font-weight:800;letter-spacing:-.01em;color:var(--g3-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.${cls}-bar{position:relative;height:8px;border-radius:999px;overflow:hidden;margin:7px 0 5px;
  background:var(--g3-track)}
.${cls}-bar>i{display:block;height:100%;width:${bar}%;border-radius:999px;
  background:linear-gradient(90deg, ${VIOLET}, ${TEAL} 55%, ${AMBER_L});
  box-shadow:0 0 12px -2px ${VIOLET}}
.${cls}-lvlMeta{font-size:11px;font-weight:700;color:var(--g3-mut);letter-spacing:.01em}
.${cls}-next{color:var(--g3-mut);font-weight:600}
/* Points-to-next-level hint — sits under the meta line, one notch quieter */
.${cls}-lvlHint{margin-top:3px;font-size:11px;font-weight:600;line-height:1.35;color:var(--g3-mut)}

/* ── Vertical dividers ───────────────────────────────────────────────── */
.${cls}-divider{align-self:stretch;width:1px;background:var(--g3-line);flex-shrink:0}

/* ── Stat segments (points / badges) ─────────────────────────────────── */
.${cls}-stat{position:relative;display:flex;flex-direction:column;gap:8px;flex:0 1 auto;min-width:0;
  text-decoration:none;color:inherit;cursor:pointer;border-radius:12px;transition:opacity .15s ease}
.${cls}-stat:hover{opacity:.82}
.${cls}-statLabel{font-size:9.5px;text-transform:uppercase;letter-spacing:.09em;font-weight:800;color:var(--g3-mut)}
.${cls}-statBody{display:flex;align-items:center;gap:11px}
.${cls}-statIco{width:34px;height:34px;border-radius:11px;display:grid;place-items:center;color:#fff;flex-shrink:0}
.${cls}-statIco.tone-gold{background:linear-gradient(140deg, ${AMBER_L}, ${AMBER});box-shadow:0 5px 14px -6px ${AMBER}}
.${cls}-statIco.tone-violet{background:linear-gradient(140deg, ${VIOLET}, ${VIOLET_D});box-shadow:0 5px 14px -6px ${VIOLET_D}}
.${cls}-statNum{font-size:22px;font-weight:900;line-height:1;letter-spacing:-.02em;color:var(--g3-ink);
  font-variant-numeric:tabular-nums}
.${cls}-statSub{font-size:10px;text-transform:uppercase;letter-spacing:.06em;font-weight:700;color:var(--g3-mut);margin-top:3px}

/* ── Badge icon strip ────────────────────────────────────────────────── */
.${cls}-badgeVal{flex-shrink:0}
.${cls}-badgeStrip{display:flex;align-items:center;gap:7px;padding-left:13px;margin-left:3px;
  border-left:1px solid var(--g3-line)}
.${cls}-badge{position:relative;width:52px;height:52px;border-radius:14px;display:grid;place-items:center;
  color:#fff;flex-shrink:0;background:linear-gradient(150deg, ${AMBER_L}, ${AMBER});
  box-shadow:0 5px 12px -6px ${AMBER}}
.${cls}-badge:nth-child(3n+2){background:linear-gradient(150deg, ${VIOLET}, ${VIOLET_D});box-shadow:0 5px 12px -6px ${VIOLET_D}}
.${cls}-badge:nth-child(3n){background:linear-gradient(150deg, ${TEAL}, ${TEAL_D});box-shadow:0 5px 12px -6px ${TEAL_D}}
.${cls}-badge img{width:100%;height:100%;object-fit:contain;padding:9px}
.${cls}-badge.locked{background:var(--g3-chip)!important;color:var(--g3-mut);box-shadow:none!important;border:1px dashed var(--g3-line)}
.${cls}-badge.locked img{filter:grayscale(1);opacity:.4}
.${cls}-badge b{position:absolute;top:-5px;right:-5px;background:#fff;color:#101828;font-size:9px;font-weight:900;
  min-width:16px;height:16px;padding:0 3px;border-radius:999px;display:grid;place-items:center;font-style:normal;
  box-shadow:0 1px 4px rgba(2,6,23,.5)}

/* ── Responsive: stack the segments below identity on narrow widths ──── */
@media (max-width:640px){
  .${cls}-card{gap:14px 18px}
  .${cls}-id{flex:1 1 100%}
  .${cls}-divider{display:none}
  .${cls}-badgeStrip{padding-left:11px}
}

/* ── Skeleton ────────────────────────────────────────────────────────── */
@keyframes ${cls}-pulse{0%,100%{opacity:1}50%{opacity:.5}}
.${cls}-skel{border-radius:11px;background:var(--g3-skel);animation:${cls}-pulse 1.5s ease-in-out infinite}
`;
}

function Skeleton({ cls }: { cls: string }) {
  return (
    <Card className={`${cls}-card`}>
      <div className={`${cls}-id`}>
        <div className={`${cls}-skel`} style={{ width: 56, height: 56, borderRadius: 999 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
          <div className={`${cls}-skel`} style={{ width: 120, height: 15 }} />
          <div className={`${cls}-skel`} style={{ height: 8, borderRadius: 999 }} />
          <div className={`${cls}-skel`} style={{ width: 90, height: 10 }} />
        </div>
      </div>
      <div className={`${cls}-divider`} />
      <div className={`${cls}-skel`} style={{ width: 120, height: 44, borderRadius: 12 }} />
      <div className={`${cls}-divider`} />
      <div className={`${cls}-skel`} style={{ width: 180, height: 44, borderRadius: 12 }} />
    </Card>
  );
}
